import { Box } from '@mui/material';
import Decimal from 'decimal.js';
import { useMemo } from 'react';

import { SHOW_ON_ROW_HOVER_CLASSNAME } from '@/components/tables/DataTable/components/ThemedDataGrid';
import { TwoLineTextRenderer } from '@/components/tables/DataTable/renderers/cell/TwoLineTextRenderer';
import { Column } from '@/components/tables/DataTable/types';
import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { LocateSuggestionButton } from '@/modules/aiSuggestions/components/LocateSuggestionButton';
import { PERSONAL_ACCOUNT_ENTITY_KINDS } from '@/modules/entities/entities.constants';
import { entityKindToDisplayName } from '@/modules/entities/EntityForm/utils';
import {
  getFederalAssetLocationDisplay,
  getStateAssetLocationDisplay,
} from '@/modules/entities/utils/getAssetLocationDisplay';
import { pickStateInEstateStatus } from '@/modules/entities/utils/pickStateInEstateStatus';
import { holdingsGroupHasContent } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupContainer.utils';
import { EstatePage_EntityFragment } from '@/modules/estate/graphql/EstatePageData.generated';
import { EntityInEstateStatus, EntityKind } from '@/types/schema';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { formatDateToMMDDYY } from '@/utils/formatting/dates';

import {
  EstateEntitiesTableColumns,
  EstateEntitiesTableRowData,
} from './EstateEntitiesTable.constants';

function isPersonalAccountKind(kind: EntityKind): boolean {
  return (PERSONAL_ACCOUNT_ENTITY_KINDS as readonly EntityKind[]).includes(
    kind
  );
}

function getEntityTypeDescription(kind: EntityKind): string {
  if (isPersonalAccountKind(kind)) {
    return 'Direct holding';
  }
  return entityKindToDisplayName(kind);
}

function getInEstateStatusFromSubtype(
  entity: EstatePage_EntityFragment
): EntityInEstateStatus | null {
  const subtype = entity.subtype;
  if ('inEstateStatus' in subtype && subtype.inEstateStatus) {
    return subtype.inEstateStatus as EntityInEstateStatus;
  }
  return null;
}

function getGstStatusFromSubtype(entity: EstatePage_EntityFragment) {
  const subtype = entity.subtype;
  if ('gstStatus' in subtype && subtype.gstStatus) {
    return subtype.gstStatus;
  }
  return null;
}

function getValuationDate(entity: EstatePage_EntityFragment): Date | null {
  // The first valuation edge holds the most recent valuation
  const valuation =
    entity.subtype.designerAccount?.valuations.edges?.[0]?.node ?? null;
  if (valuation?.effectiveDate) {
    return valuation.effectiveDate instanceof Date
      ? valuation.effectiveDate
      : new Date(valuation.effectiveDate);
  }
  return null;
}

function shouldIncludeEntity(entity: EstatePage_EntityFragment): boolean {
  if (!isPersonalAccountKind(entity.kind)) {
    return true;
  }
  return holdingsGroupHasContent(entity, []);
}

export function mapEntitiesToRows(
  entities: EstatePage_EntityFragment[]
): EstateEntitiesTableRowData[] {
  return entities.filter(shouldIncludeEntity).map((entity) => {
    const rawValue = entity.subtype.currentValue;
    const currentValue =
      rawValue instanceof Decimal ? rawValue : new Decimal(rawValue ?? 0);
    const inEstateStatus = getInEstateStatusFromSubtype(entity);
    const stateInEstateStatus = pickStateInEstateStatus(entity.subtype);
    const gstStatus = getGstStatusFromSubtype(entity);

    const locationInput = {
      inEstateStatus,
      stateInEstateStatus,
      gstStatus,
    };
    const federal = getFederalAssetLocationDisplay(locationInput);
    const state = getStateAssetLocationDisplay(locationInput);

    const valuationDate = getValuationDate(entity);

    return {
      id: entity.id,
      entityId: entity.id,
      name: entity.subtype.displayName,
      entityTypeDescription: getEntityTypeDescription(entity.kind),
      federalAssetLocationDisplay:
        federal.locationDisplay ?? EMPTY_CONTENT_HYPHEN,
      stateAssetLocationDisplay: state.locationDisplay ?? EMPTY_CONTENT_HYPHEN,
      currentValue,
      currentValueDisplay: formatCurrencyNoDecimals(currentValue),
      valuationDateDisplay: valuationDate
        ? `as of ${formatDateToMMDDYY(valuationDate)}`
        : undefined,
      onboardingSuggestionId:
        entity.onboardingEntitySuggestion?.[0]?.id ?? null,
    };
  });
}

type LocateSuggestionHandler = (suggestionId: string) => void;

interface EstateEntitiesTableSuggestionLocatorButtonProps {
  row: EstateEntitiesTableRowData;
  onLocateSuggestionClick?: LocateSuggestionHandler;
}

function EstateEntitiesTableSuggestionLocatorButton({
  row,
  onLocateSuggestionClick,
}: EstateEntitiesTableSuggestionLocatorButtonProps) {
  if (!row.onboardingSuggestionId || !onLocateSuggestionClick) {
    return null;
  }

  return (
    <Box
      className={SHOW_ON_ROW_HOVER_CLASSNAME}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <LocateSuggestionButton
        suggestionID={row.onboardingSuggestionId}
        onLocateSuggestionClick={onLocateSuggestionClick}
        dataTestId={`all-entities-locate-suggestion-${row.entityId}`}
      />
    </Box>
  );
}

export function useColumns(
  onLocateSuggestionClick?: LocateSuggestionHandler
): Column<EstateEntitiesTableRowData>[] {
  return useMemo(
    () => [
      {
        field: EstateEntitiesTableColumns.NAME,
        headerName: 'Name',
        flex: 1.5,
        renderCell: TwoLineTextRenderer({
          lineOne: ({ row }) => row.name,
          lineTwo: ({ row }) => row.entityTypeDescription,
          textAccompaniment: ({ row }) => (
            <EstateEntitiesTableSuggestionLocatorButton
              row={row}
              onLocateSuggestionClick={onLocateSuggestionClick}
            />
          ),
        }),
      },
      {
        field: EstateEntitiesTableColumns.FEDERAL_ASSET_LOCATION,
        headerName: 'Federal asset location',
        flex: 1,
        renderCell: TwoLineTextRenderer({
          lineOne: ({ row }) => row.federalAssetLocationDisplay,
        }),
      },
      {
        field: EstateEntitiesTableColumns.STATE_ASSET_LOCATION,
        headerName: 'State asset location',
        flex: 1,
        renderCell: TwoLineTextRenderer({
          lineOne: ({ row }) => row.stateAssetLocationDisplay,
        }),
      },
      {
        field: EstateEntitiesTableColumns.VALUE,
        headerName: 'Value',
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        renderCell: TwoLineTextRenderer({
          lineOne: ({ row }) => row.currentValueDisplay,
          lineTwo: ({ row }) => row.valuationDateDisplay,
        }),
      },
    ],
    [onLocateSuggestionClick]
  );
}
